// Firebase initialization wrapper to handle code-splitting race condition
import type { FirebaseApp } from 'firebase/app';
import type { Auth } from 'firebase/auth';

let firebaseApp: FirebaseApp | null = null;
let firebaseAuth: Auth | null = null;
let initPromise: Promise<void> | null = null;

// Initialize Firebase with proper module loading
export async function initializeFirebase(): Promise<void> {
  if (initPromise) return initPromise;
  
  initPromise = (async () => {
    try {
      // Dynamically import Firebase modules to ensure proper loading order
      const [{ initializeApp }, { getAuth, connectAuthEmulator, setPersistence, browserSessionPersistence }] = await Promise.all([
        import('firebase/app'),
        import('firebase/auth')
      ]);

      // Firebase configuration - require environment variables in production
      const isProduction = import.meta.env.PROD;

      const getRequiredEnv = (key: string, devFallback: string): string => {
        const value = import.meta.env[key];
        if (value && value !== 'undefined') return value;
        if (isProduction) {
          throw new Error(`${key} environment variable is required in production`);
        }
        return devFallback;
      };

      const firebaseConfig = {
        apiKey: getRequiredEnv('VITE_FIREBASE_API_KEY', 'AIzaSyDn1CIbR0Q6CRPQ4SzuVJUJj09P7bhXZfk'),
        authDomain: getRequiredEnv('VITE_FIREBASE_AUTH_DOMAIN', 'digistore-dev-463319.firebaseapp.com'),
        projectId: getRequiredEnv('VITE_FIREBASE_PROJECT_ID', 'digistore-dev-463319'),
        storageBucket: getRequiredEnv('VITE_FIREBASE_STORAGE_BUCKET', 'digistore-dev-463319.firebasestorage.app'),
        messagingSenderId: getRequiredEnv('VITE_FIREBASE_MESSAGING_SENDER_ID', '691438034833'),
        appId: getRequiredEnv('VITE_FIREBASE_APP_ID', '1:691438034833:web:79a665e3da467c26a34430'),
      };

      // Initialize Firebase
      firebaseApp = initializeApp(firebaseConfig);
      firebaseAuth = getAuth(firebaseApp);

      // Set persistence to SESSION — clears on tab close, prevents token theft via XSS
      await setPersistence(firebaseAuth, browserSessionPersistence);

      // Connect to emulator in development
      if (import.meta.env.DEV && import.meta.env.VITE_USE_AUTH_EMULATOR === 'true') {
        connectAuthEmulator(firebaseAuth, 'http://localhost:9099');
      }

      console.log('Firebase initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Firebase:', error);
      throw error;
    }
  })();

  return initPromise;
}

// Get Firebase app instance
export async function getFirebaseApp(): Promise<FirebaseApp> {
  if (!firebaseApp) {
    await initializeFirebase();
  }
  if (!firebaseApp) {
    throw new Error('Firebase app not initialized');
  }
  return firebaseApp;
}

// Get Firebase auth instance
export async function getFirebaseAuth(): Promise<Auth> {
  if (!firebaseAuth) {
    await initializeFirebase();
  }
  if (!firebaseAuth) {
    throw new Error('Firebase auth not initialized');
  }
  return firebaseAuth;
}